"use client";

import { useRef } from "react";
import Image from "next/image";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import Container from "@/components/ui/container";
import { siteContent } from "@/lib/data";

export default function Testimonials() {
  const { testimonials, testimonialsMeta } = siteContent;
  const trackRef = useRef<HTMLUListElement>(null);

  const scrollBy = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const step = track.clientWidth * 0.8;
    track.scrollBy({ left: step * direction, behavior: "smooth" });
  };

  return (
    <section className="py-14 sm:py-20">
      <Container>
        <div className="flex items-start justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[color:var(--color-text)] leading-tight">
              {testimonialsMeta.title}
            </h2>
            <a
              href="#"
              className="mt-2 inline-flex items-center gap-2 text-sm underline underline-offset-4 text-[color:var(--color-text)] hover:text-[color:var(--color-primary)]"
            >
              <Star
                size={15}
                className="fill-[color:var(--color-accent)] text-[color:var(--color-accent)]"
              />
              {testimonialsMeta.ratingText}
            </a>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              aria-label="Previous testimonials"
              onClick={() => scrollBy(-1)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--color-border-strong)] bg-[color:var(--color-surface)] text-[color:var(--color-text)] hover:border-[color:var(--color-primary)] hover:text-[color:var(--color-primary)]"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Next testimonials"
              onClick={() => scrollBy(1)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--color-border-strong)] bg-[color:var(--color-surface)] text-[color:var(--color-text)] hover:border-[color:var(--color-primary)] hover:text-[color:var(--color-primary)]"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <ul
          ref={trackRef}
          className="grid grid-flow-col auto-cols-[85%] sm:auto-cols-[48%] lg:auto-cols-[calc((100%-3*1.5rem)/4)] gap-5 sm:gap-6 overflow-x-auto snap-x snap-mandatory pb-2"
        >
          {testimonials.map((t) => (
            <li
              key={t.id}
              className="flex flex-col gap-3 snap-start"
            >
              <div className="text-sm font-semibold text-[color:var(--color-text)]">
                {t.author}
              </div>
              <div className="flex items-center gap-1" aria-label={`${t.rating} out of 5`}>
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    className="fill-[color:var(--color-accent)] text-[color:var(--color-accent)]"
                  />
                ))}
              </div>
              <p className="text-sm leading-relaxed text-[color:var(--color-muted)]">
                {t.quote}
              </p>
              <div className="mt-auto pt-2">
                <div className="relative h-14 w-20 overflow-hidden bg-[color:var(--color-surface-alt)]">
                  <Image
                    src={t.image.url}
                    alt={t.image.altText}
                    fill
                    sizes="80px"
                    className="object-cover"
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      </Container>
    </section>
  );
}
